import {
  type NexiRetrievePaymentResponse,
  type Option,
  type OrderDTO,
  type PaymentProviderConfig,
  type ShippingProviderConfig
} from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { nexiApi } from '../../../utils/nexi'
import { createError } from '../../../utils/fetching'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult } from './types'
import {
  generateIngridShippingProviderResult,
  generateNexiAddresses,
  generatePostnordShippingProviderResult
} from './utils'

export interface ProvisionOrderNexiProviderResult {
  nexiResponse: NexiRetrievePaymentResponse
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: OrderProvisionResult['shippingProviderResult']
}

export const provisionOrderNexiProvider = async (
  paymentId: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig
): Promise<Option<ProvisionOrderNexiProviderResult>> => {
  // 1. Confirmation in Nexi
  const { error: nexiError, data: nexiPaymentResponse } =
    await nexiApi.checkout.retrievePayment(paymentId)

  if (nexiError || !nexiPaymentResponse) {
    throw Error(nexiError || 'Nexi GET endpoint error')
  }

  // 2. Checks if the order exists
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: nexiPaymentResponse.payment.orderDetails.reference
  })

  // 3. If the order doesn't exist, returns an error
  if (getOrderError || !orderData) {
    throw createError(getOrderError ?? 'BE GET endpoint error')
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateNexiAddresses(nexiPaymentResponse)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult: OrderProvisionResult['shippingProviderResult'] | void =
    await getShippingProviderResult()

  // 5. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'NEXI',
      providerResponse: {
        nexiResponse: nexiPaymentResponse
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    nexiResponse: nexiPaymentResponse,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
